import React from 'react';
import { CalendarClock, AlertCircle } from 'lucide-react';
import {
    DESFECHOS, avisoDoDesfecho, faltaNoDesfecho,
    type CamposDesfecho, type DesfechoContato,
} from '../lib/desfechoContato';

interface Props {
    valor: CamposDesfecho;
    onChange: (proximo: CamposDesfecho) => void;
    /** 'YYYY-MM-DD' de hoje, no fuso local. */
    hoje: string;
    /** 'YYYY-MM-DD' de amanhã — o retorno começa nele. */
    amanha: string;
    /** Cada formulário mantém o próprio visual (modal grande, painel compacto). */
    inputClassName: string;
    labelClassName: string;
}

/** Cor de cada escolha — as mesmas dos botões de status do Gerenciar Cobrança. */
const COR_ATIVA: Record<DesfechoContato, string> = {
    retorno: 'bg-primary/10 text-primary border-primary/60 dark:bg-primary/25 dark:text-primary-light',
    negociando: 'bg-amber-50 text-amber-700 border-amber-400 dark:bg-amber-900/20 dark:text-amber-400',
    negociado: 'bg-indigo-50 text-indigo-700 border-indigo-400 dark:bg-indigo-900/20 dark:text-indigo-400',
    pago: 'bg-emerald-50 text-emerald-700 border-emerald-400 dark:bg-emerald-900/20 dark:text-emerald-400',
};

/**
 * "Como ficou?" do registro de contato (#6165): todo contato sai com data de
 * retorno ou com status. Sem um dos dois o botão de registrar não libera —
 * e o banco também recusa.
 */
const DesfechoContatoCampos: React.FC<Props> = ({ valor, onChange, hoje, amanha, inputClassName, labelClassName }) => {
    const escolher = (desfecho: DesfechoContato) => {
        if (desfecho === valor.desfecho) return;
        const trocouDeFinanceiro = (desfecho === 'pago') !== (valor.desfecho === 'pago');
        onChange({
            ...valor,
            desfecho,
            // Valor e data do acordo não servem como valor e data de recebimento,
            // nem o contrário. Pagamento costuma ser conferido no dia.
            valor: trocouDeFinanceiro ? '' : valor.valor,
            dataPagamento: desfecho === 'pago'
                ? (trocouDeFinanceiro ? hoje : valor.dataPagamento)
                : (trocouDeFinanceiro ? '' : valor.dataPagamento),
        });
    };

    const falta = faltaNoDesfecho(valor, hoje);
    const aviso = falta ? null : avisoDoDesfecho(valor);
    const comData = valor.desfecho === 'retorno' || valor.desfecho === 'negociando';

    return (
        <div>
            <span className={labelClassName}>
                Como ficou? <span className="text-red-500">*</span>
            </span>
            <div className="flex flex-wrap gap-1.5" role="radiogroup" aria-label="Como ficou o cliente depois do contato">
                {DESFECHOS.map(op => {
                    const ativo = op.valor === valor.desfecho;
                    return (
                        <button
                            key={op.valor}
                            type="button"
                            role="radio"
                            aria-checked={ativo}
                            onClick={() => escolher(op.valor)}
                            className={`px-3 py-1.5 rounded-lg text-xs font-bold border transition-colors ${ativo
                                ? COR_ATIVA[op.valor]
                                : 'border-gray-200 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:border-gray-300 dark:hover:border-gray-500'}`}
                        >
                            {op.rotulo}
                        </button>
                    );
                })}
            </div>

            <div className="grid grid-cols-2 gap-2 mt-2">
                {comData ? (
                    <label className="col-span-2 sm:col-span-1">
                        <span className={labelClassName}>Próximo contato</span>
                        <input
                            type="date"
                            value={valor.proximoContato}
                            min={amanha}
                            onChange={e => onChange({ ...valor, proximoContato: e.target.value })}
                            className={inputClassName}
                        />
                    </label>
                ) : (
                    <>
                        <label>
                            <span className={labelClassName}>
                                {valor.desfecho === 'negociado' ? 'Valor combinado (R$)' : 'Valor recebido (R$)'}
                            </span>
                            <input
                                type="number"
                                min="0"
                                step="0.01"
                                value={valor.valor}
                                placeholder={valor.desfecho === 'pago' ? 'Opcional' : '0,00'}
                                onChange={e => onChange({ ...valor, valor: e.target.value })}
                                className={inputClassName}
                            />
                        </label>
                        <label>
                            <span className={labelClassName}>
                                {valor.desfecho === 'negociado' ? 'Pagar até' : 'Recebido em'}
                            </span>
                            <input
                                type="date"
                                value={valor.dataPagamento}
                                min={valor.desfecho === 'negociado' ? hoje : undefined}
                                max={valor.desfecho === 'pago' ? hoje : undefined}
                                onChange={e => onChange({ ...valor, dataPagamento: e.target.value })}
                                className={inputClassName}
                            />
                        </label>
                    </>
                )}
            </div>

            <p className={`flex items-start gap-1.5 text-[11px] mt-1.5 ${falta
                ? 'text-amber-600 dark:text-amber-400'
                : 'text-gray-500 dark:text-gray-400'}`}>
                {falta
                    ? <AlertCircle size={13} className="shrink-0 mt-px" />
                    : <CalendarClock size={13} className="shrink-0 mt-px" />}
                {falta ?? aviso}
            </p>
        </div>
    );
};

export default DesfechoContatoCampos;
